import React, { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate, useParams } from 'react-router'
import { observer } from 'mobx-react-lite'
import classNames from 'classnames'

// evertel
import { DocumentServiceProvider } from '@evertel/service'
import { useService } from '@evertel/di'
import { Document, WidgetsProvider } from '@evertel/schema-parser'
import { DocumentCreateController, EverdocMediaUplaodError } from '@evertel/document'
import { WriteWidgets } from './schema-widgets'
import { DocumentIcon, DocumentPlaceholder } from './elements'
import {
    Button,
    Text,
    InfoBox,
    PageBody,
    Breadcrumb,
    Input,
    Card,
    CardHeader,
    CardBody,
    CardHeaderActions,
    useUI,
    CardFooter,
    Icon
} from '@evertel/web/ui'
import { SessionService } from '@evertel/session/SessionService'
import { APIDataDocument, APIDataDocumentSchema } from '@evertel/types'


const DocumentCreate = observer(() => {

    const { schemaId } = useParams()
    const { addToast } = useUI()
    const navigate = useNavigate()

    const [isLoading, setIsLoading] = useState(true)
    const [isSaving, setIsSaving] = useState(false)
    const [hasChanged, setHasChanged] = useState(false)
    const [documentName, setDocumentName] = useState('')
    const [documentNameError, setDocumentNameError] = useState(false)


    const documentCreateController = useService(DocumentCreateController, [schemaId])
    const sessionService = useService(SessionService, [])

    useEffect(() => {
        (async () => {
            if (!schemaId) return

            setIsLoading(true)
            await documentCreateController.init(
                sessionService.selectedDepartmentId,
                Number(schemaId),
                'web'
            )
            setDocumentName(documentCreateController.name)
            setIsLoading(false)
        })()
    }, [documentCreateController, sessionService])

    const onChangeName = (e: any) => {
        setDocumentNameError(false)
        setDocumentName(e?.target?.value)
        documentCreateController.setName(e?.target?.value)
    }

    const onSave = async () => {
        setIsSaving(true)
        try {

            if (!documentName.trim()) {
                setDocumentNameError(true)
                throw new Error('Document name is required')
            }

            const { id } = await documentCreateController.create()

            // take them to the edit screen
            navigate(`/everdocs/edit/${id}`)

        } catch (error: any) {
            if (error instanceof EverdocMediaUplaodError) {
                addToast({
                    color: 'warning',
                    title: 'Be aware',
                    message: `Document created with some media issues. \n ${error.message}`
                })

                // take them to the edit screen
                navigate(`/everdocs/edit/${error?.document?.id}`)

            } else {
                addToast({
                    color: 'danger',
                    title: 'Something went wrong',
                    message: error.message
                })
            }

            return
        } finally {
            setIsSaving(false)
        }
    }

    const onChange = useCallback((value: Record<string, any>): void => {
        setHasChanged(true)
        documentCreateController.updateData(value)
    }, [documentCreateController])

    if (isLoading) {
        return (
            <DocumentPlaceholder />
        )
    }

    return (
        <PageBody>
            <Breadcrumb
                items={[
                    { label: 'EverDocs', onClick: () => navigate('/everdocs') },
                    { label: `Create "${documentCreateController?.documentSchema?.name}"`, active: true }
                ]}
            />
            <Card>
                <CardHeader className="flex-wrap gap-2">
                    <div className="d-flex" style={{ minWidth: '80%' }}>
                        <DocumentIcon
                            schema={documentCreateController?.documentSchema as APIDataDocumentSchema}
                            width={40}
                            height={44}
                        />
                        <div className="w-100">
                            <Input
                                style={{ borderColor: documentNameError ? 'red' : undefined }}
                                className={classNames(
                                    'form-control h3',
                                    { 'is-invalid': documentNameError }
                                )}
                                value={documentName}
                                required
                                onChange={onChangeName}
                                onFocus={(e) => e.target.setSelectionRange(e.target.value.length, e.target.value.length)}
                            />
                            {documentNameError &&
                                <div className="invalid-feedback">
                                    Document Name is required.
                                </div>
                            }
                        </div>
                    </div>
                    <CardHeaderActions>
                        <Button
                            color="primary"
                            loading={isSaving}
                            onClick={onSave}>
                            <Text
                                bold>
                                Save
                            </Text>
                        </Button>
                    </CardHeaderActions>
                </CardHeader>
                <CardBody>
                    {(!!documentCreateController.errors) &&
                        <InfoBox color="danger">
                            <Text>
                                {`Please review the document, there ${(documentCreateController.errorsCount === 1) ?
                                    'is 1 error' : `are ${documentCreateController.errorsCount} errors`}`}
                            </Text>
                        </InfoBox>
                    }
                    <DocumentServiceProvider services={[{ id: 'meta', value: {} }]}>
                        <WidgetsProvider value={WriteWidgets}>
                            <Document
                                key={schemaId}
                                layout="full"
                                schema={documentCreateController.documentSchema?.schema}
                                schemaId={documentCreateController.documentSchemaId}
                                value={documentCreateController.data}
                                onChange={onChange}
                                errors={documentCreateController.errors}
                            />
                        </WidgetsProvider>
                    </DocumentServiceProvider>
                </CardBody>
                <CardFooter>
                    <span aria-hidden="true"><Icon size="xs" name="asterisk" color="danger" faProps={{title: 'Required'}}/> = required</span>
                </CardFooter>
            </Card>
        </PageBody>
    )
})

export {
    DocumentCreate
}
